// Adds a YouTube video to this tab's songs: its chords come from the API server (ADR 0008).
import { getAnalysis, getSong, markOpened, putAnalysis, putSong } from '@/storage/songsRepo';
import type { Song } from '@/storage/types';
import { useImportStore, type ImportHandle } from '@/features/upload';
import { useSettingsStore } from '@/state/settingsStore';
import { CHORDS_API } from './config';
import { ChordsApiError, fetchVideoChords, type ChordModel, type JobStage } from './chordsApi';

/** Library id of a YouTube song (files use their content hash); one per chord model. */
export const youtubeSongId = (videoId: string, model: ChordModel) =>
  `youtube:${videoId}${model === 'btc' ? ':btc' : ''}`;

/** The model the settings ask for. */
const chosenModel = (): ChordModel =>
  useSettingsStore.getState().chordModel === 'btc' ? 'btc' : 'dsp';

export function startYouTubeImport(
  videoId: string,
  options: {
    onStage?: (stage: JobStage, position: number) => void;
    onTitle?: (title: string) => void;
  } = {},
): ImportHandle {
  const controller = new AbortController();
  const run = async () => {
    let model = chosenModel();
    const id = youtubeSongId(videoId, model);
    const existing = await getSong(id);
    const cached = existing ? await getAnalysis(id) : undefined;
    if (existing && cached) {
      await markOpened(id);
      return { song: existing, result: cached, fromCache: true };
    }
    const fetchWith = (m: ChordModel) =>
      fetchVideoChords(CHORDS_API, videoId, {
        signal: controller.signal,
        onStage: (stage, position) => options.onStage?.(stage, position),
        model: m,
      });
    let fetched;
    try {
      fetched = await fetchWith(model);
    } catch (e) {
      // The AI model is off or down on the server: use the classic engine instead.
      if (model !== 'btc' || !(e instanceof ChordsApiError) || e.code !== 'model_unavailable')
        throw e;
      model = 'dsp';
      fetched = await fetchWith(model);
    }
    const { title, result } = fetched;
    if (title) options.onTitle?.(title);
    const now = Date.now();
    const song: Song = {
      id: youtubeSongId(videoId, model),
      title: title ?? 'YouTube video',
      fileName: 'YouTube',
      fileSize: 0,
      mimeType: 'video/youtube',
      youtubeId: videoId,
      duration: result.duration,
      summary: {
        key: result.key,
        bpm: result.beatGrid.bpm,
        chordChanges: result.segments.filter((s) => s.chord).length,
      },
      createdAt: now,
      lastOpenedAt: now,
    };
    await putSong(song);
    await putAnalysis(song.id, result);
    return { song, result, fromCache: false };
  };
  return {
    outcome: run(),
    cancel() {
      controller.abort();
    },
  };
}

/** Starts importing a video through the usual progress screen (the caller shows it). */
export function importYouTube(videoId: string): Promise<void> {
  return useImportStore
    .getState()
    .startRemote(
      { songId: youtubeSongId(videoId, chosenModel()), fileName: 'YouTube', fileSize: 0 },
      (progress) =>
        startYouTubeImport(videoId, {
          onStage: progress.stage,
          onTitle: (title) => {
            progress.info({ title });
          },
        }),
    );
}
